import { values } from 'lodash';
import moment from 'moment';
import React from 'react';
import { CareTypeNames } from '../../Components/CareTypeNames';
import { QuickEmailLink } from '../../Components/Flair';
import { LoadingPage } from '../../Components/LoadingOverlay';
import { Text } from '../../globalStyles';
import { CareType, EngagementTag, useAdminNeedsPatientOutreachQuery } from '../../graphQL';
import { useCareTypeConfigContext } from '../../Components/CareTypeConfigContext';
import { getFullNameReversed } from '../../modelUtils/users';
import { dateSorterFactory } from '../../utils';
import { DashboardTable } from '../Home/DashTable';
import { EmptyState } from '../Home/EmptyState';
import { EngagementPill, engagementTagCopy } from '../Home/Widgets/EngagementTag';
import * as Styles from '../Home/styles';
import { UnstyledLink } from '../Shared';
const emptyState = <EmptyState text="No actions" icon="safety" />;

export const NeedsOutreach = () => {
  const { data, loading } = useAdminNeedsPatientOutreachQuery();
  const { careTypeOptions } = useCareTypeConfigContext();

  if (loading) return <LoadingPage />;
  if (!data) return null;

  const outreachUsers = data.adminNeedsPatientOutreach;

  return (
    <Styles.SectionContainer>
      <Text.h3 className="mb2">Needs Patient Outreach</Text.h3>
      <DashboardTable
        data={outreachUsers}
        fullHeight={true}
        emptyState={emptyState}
        columns={[
          {
            title: 'Patient',
            render: user => (
              <>
                <UnstyledLink to={`/users/${user.id}`} className="mr2">
                  <Text.bodySmall className="b">{getFullNameReversed(user)}</Text.bodySmall>
                </UnstyledLink>
                <QuickEmailLink user={user} />
              </>
            ),
          },
          {
            title: 'Care Type',
            render: user => (
              <Text.bodySmall>
                <CareTypeNames careTypes={user.careTypes} />
              </Text.bodySmall>
            ),
          },
          {
            title: 'Status',
            render: user => <Text.bodySmall>{user.taskStatus}</Text.bodySmall>,
          },
          {
            title: 'Last Outreach',
            initialSort: true,
            sort: dateSorterFactory(u => u.lastOutreachAttempt, 'DESC'),
            render: ({ lastOutreachAttempt }) => (
              <Text.bodySmall>
                {lastOutreachAttempt ? moment(lastOutreachAttempt).format('L') : '--'}
              </Text.bodySmall>
            ),
          },
          {
            render: user =>
              user.engagementTags
                .filter(e => e !== EngagementTag.Unverified)
                .map(e => <EngagementPill key={e} tag={e} />),
          },
        ]}
        getKey={user => user.id}
        filters={[
          {
            key: 'careTypeFilter',
            options: careTypeOptions,
            placeholder: 'Care Type',
            test: (user, v) => user.careTypes.includes(v as CareType),
          },
          {
            key: 'statusFilter',
            options: ['Intake', 'Ongoing Care'].map(status => ({ id: status, label: status })),
            placeholder: 'Status',
            test: (user, v) => user.taskStatus === v,
          },
          {
            key: 'eventFilter',
            options: values(engagementTagCopy).map(event => ({ id: event, label: event })),
            placeholder: 'Event',
            test: (user, v) => user.engagementTags.map(t => engagementTagCopy[t]).includes(v),
          },
        ]}
      />
    </Styles.SectionContainer>
  );
};
